/**
 * DeployInputsSection — the "Required inputs" section of the marketplace
 * deploy modal. GENERIC + registry-driven: it reads a template's
 * `inputSchema`, extracts the fields annotated with `deployInput`, and
 * renders one control per field via the source→picker registry. flaky-
 * test-fixer is just the first consumer; ANY template that declares
 * `deployInput` annotations gets this section for free.
 *
 * Backward compatible: a template with NO deploy-input fields renders
 * NOTHING (returns null), so existing agents (sentry-triage, code-review)
 * deploy exactly as before with no new section.
 *
 * Lifecycle / wiring:
 *   - `inputSchema`  — the template's JSON Schema (entry.inputSchema).
 *   - `values`       — { fieldName: value } collected so far (parent state).
 *   - `onChange(name, val)` — update one field's value in parent state.
 *   - `errors`       — { fieldName: message } to render inline (the parent
 *                      flags required-but-empty fields on a Deploy click,
 *                      same one-click-surfaces-everything pattern the
 *                      integration gate uses).
 *   - `disabled`     — deploy in flight.
 *   - `sectionRef`   — so the parent can scrollIntoView on a blocked deploy.
 *
 * The parent owns validation (validateDeployInputs) + the deploy gate; this
 * component is pure rendering over the registry.
 */

import React, { useMemo } from 'react';
import { collectDeployInputFields, pickerForField } from './deployInputSources';
import './DeployInputsSection.css';

function humanize(name) {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

export default function DeployInputsSection({
  inputSchema,
  values = {},
  onChange,
  errors = {},
  disabled = false,
  sectionRef,
}) {
  const fields = useMemo(() => collectDeployInputFields(inputSchema), [inputSchema]);

  // Backward-compat: no deploy-input annotations → render nothing.
  if (fields.length === 0) return null;

  return (
    <section className="mkt-modal-section" ref={sectionRef}>
      <h3 className="mkt-modal-section-title">Required inputs</h3>
      <div className="deploy-inputs">
        {fields.map((f) => {
          const Picker = pickerForField(f.deployInput);
          const label = f.deployInput.label || f.schema.title || humanize(f.name);
          const help = f.deployInput.help || f.schema.description || '';
          const err = errors[f.name];
          return (
            <div
              key={f.name}
              className={`deploy-input-field ${err ? 'has-error' : ''}`}
            >
              <label className="deploy-input-label" htmlFor={`deploy-input-${f.name}`}>
                {label}
                {f.required && (
                  <span className="deploy-input-required" aria-label="required">*</span>
                )}
                {help && <span className="deploy-input-help">{help}</span>}
              </label>
              <Picker
                name={f.name}
                schema={f.schema}
                deployInput={f.deployInput}
                value={values[f.name]}
                onChange={(val) => onChange(f.name, val)}
                disabled={disabled}
                error={err}
                allValues={values}
              />
              {err && <p className="deploy-input-error">{err}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
